import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Map, MapPin } from 'lucide-react';
import { isWithinInterval, startOfToday, endOfToday, startOfWeek, endOfWeek, startOfMonth, endOfMonth, Interval } from 'date-fns';

import BlogCard from '../components/blog/BlogCard';
import GoogleAdUnit from '../components/common/GoogleAdUnit';
import { Record, RecordCategory } from '../types/records';
import { getRecords } from '../services/recordService';
import { trackUniqueStat } from '../services/achievementService';

const provinces = ['Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Amasya', 'Ankara', 'Antalya', 'Artvin', 'Aydın', 'Balıkesir', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkâri', 'Hatay', 'Isparta', 'Mersin', 'İstanbul', 'İzmir', 'Kars', 'Kastamonu', 'Kayseri', 'Kırklareli', 'Kırşehir', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Kahramanmaraş', 'Mardin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Rize', 'Sakarya', 'Samsun', 'Siirt', 'Sinop', 'Sivas', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Şanlıurfa', 'Uşak', 'Van', 'Yozgat', 'Zonguldak', 'Aksaray', 'Bayburt', 'Karaman', 'Kırıkkale', 'Batman', 'Şırnak', 'Bartın', 'Ardahan', 'Iğdır', 'Yalova', 'Karabük', 'Kilis', 'Osmaniye', 'Düzce'];
const timeFilters = ['Tümü', 'Bugün', 'Bu Hafta', 'Bu Ay'];
const categories: (RecordCategory | 'Tümü')[] = ['Tümü', 'Etkinlikler', 'Doğa Güzellikleri', 'Konserler', 'Beklenmedik Olaylar', 'Devletçe Kapalı', 'Popüler Olanlar'];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const BlogPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [timeFilter, setTimeFilter] = useState('Tümü');
  const [categoryFilter, setCategoryFilter] = useState<RecordCategory | 'Tümü'>('Tümü');

  const [filteredRecords, setFilteredRecords] = useState<Record[]>([]);
  const allRecords = useMemo(() => getRecords(), []);

  const featuredRecords = useMemo(() =>
    allRecords.filter(record => record.isFeatured).slice(0, 3),
    [allRecords]
  );

  useEffect(() => {
    let results = allRecords;

    // 1. Filter by City
    if (selectedCity) { // Only filter by city if a city is actually selected
      results = results.filter(record => record.cityName === selectedCity);
    }

    // 2. Filter by Time
    const today = new Date();
    if (timeFilter !== 'Tümü') { // Assuming 'Tümü' for time would show all
      results = results.filter(record => {
        // Timeless records (like nature spots) should always appear regardless of time filter
        if (!record.startDate && !record.endDate) {
          return true;
        }

        const recordStartDate = record.startDate || record.endDate;
        const recordEndDate = record.endDate || record.startDate;

        if (!recordStartDate || !recordEndDate) return false;

        let interval: Interval | null = null;
        if (timeFilter === 'Bugün') {
          interval = { start: startOfToday(), end: endOfToday() };
        } else if (timeFilter === 'Bu Hafta') {
          interval = { start: startOfWeek(today, { weekStartsOn: 1 }), end: endOfWeek(today, { weekStartsOn: 1 }) };
        } else if (timeFilter === 'Bu Ay') {
          interval = { start: startOfMonth(today), end: endOfMonth(today) };
        }

        return interval ? isWithinInterval(recordStartDate, interval) || isWithinInterval(recordEndDate, interval) : true;
      });
    }

    // 3. Filter by Category
    if (categoryFilter !== 'Tümü') {
      if (categoryFilter === 'Popüler Olanlar') {
        results = results.filter(record => record.isFeatured);
      } else {
        results = results.filter(record => record.category === categoryFilter);
      }
    }

    setFilteredRecords(results);
  }, [selectedCity, timeFilter, categoryFilter, allRecords]);

  // Başarım Takibi: Hafta Sonu Kaşifi
  useEffect(() => {
    if (selectedCity) {
      trackUniqueStat('visitedCities', selectedCity, 3, 'weekend-explorer').then(unlocked => {
        if (unlocked) {
          alert("Tebrikler! 'Hafta Sonu Kaşifi' başarımını kazandınız! 🌍");
        }
      });
    }
  }, [selectedCity]);


  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || provinces.includes(value)) { // Allow clearing the input
      setSelectedCity(value);
    }
  }

  const handleGoToPlan = () => {
    // Navigate only if a city is selected, otherwise, it might not make sense
    if (selectedCity) {
      navigate(`/start-plan?destination=${selectedCity}`);
    } else {
      alert("Lütfen bir şehir seçin."); // Or handle this case as per UX requirements
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">

      {/* Hero Section */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-center md:text-left md:w-2/3">
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                Türkiye Etkinlik & Durum Rehberi
              </h1>
              <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
                81 ilde etkinlikler, doğa güzellikleri, beklenmedik olaylar ve kapalı mekânlar.
              </p>
              <div className="mt-6">
                <button onClick={handleGoToPlan} className="inline-block bg-blue-100 text-blue-800 font-semibold px-6 py-2 rounded-full hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-900 transition-colors">
                  Bu Şehir İçin Plan Oluştur
                </button>
              </div>
            </div>
            <div className="hidden md:flex md:w-1/3 justify-center md:justify-end mt-8 md:mt-0">
              <Map size={128} className="text-gray-300 dark:text-gray-600" />
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content Column */}
          <div className="lg:col-span-3">
            {/* Filters Section */}
            <section aria-labelledby="filter-heading" className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
              <h2 id="filter-heading" className="sr-only">İçerik Filtreleri</h2>
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <label htmlFor="city-select" className="block text-sm font-medium text-gray-700 dark:text-gray-200">İl Seçin</label>
                  <input id="city-select" list="tr-provinces" defaultValue={selectedCity} onBlur={handleCityChange} placeholder="İl yazın veya seçin" className="mt-1 w-full h-12 px-4 rounded-lg bg-gray-100 dark:bg-gray-700 border-2 border-transparent focus:border-blue-500 outline-none" />
                  <datalist id="tr-provinces">{provinces.map(p => <option key={p} value={p} />)}</datalist>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex items-center">
                    <MapPin size={14} className="mr-1.5" />
                    Şu an <span className="font-semibold mx-1">{selectedCity || 'tüm şehirler'}</span> için sonuçlar görüyorsunuz.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Zaman Seçimi</label>
                  <div className="mt-1 inline-flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 space-x-1">
                    {timeFilters.map(time => (
                      <button key={time} onClick={() => setTimeFilter(time)} className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${timeFilter === time ? 'bg-white text-blue-600 shadow' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Category Chips */}
            <section aria-labelledby="category-heading" className="mt-6">
              <h2 id="category-heading" className="sr-only">Kategoriler</h2>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button key={cat} onClick={() => setCategoryFilter(cat)} className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors border-2 ${categoryFilter === cat ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-500 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:border-blue-500'}`}>
                    {cat}
                  </button>
                ))}
              </div>
            </section>

            {/* Content Cards */}
            <section aria-labelledby="content-heading" className="mt-8">
              <h2 id="content-heading" className="sr-only">{selectedCity} için içerikler</h2>
              <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-8" variants={containerVariants} initial="hidden" animate="visible">
                {filteredRecords.length > 0 ? (
                  filteredRecords.map(record => (
                    <BlogCard key={record.id} post={record} variants={itemVariants} />
                  ))
                ) : (
                  <div className="col-span-full text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
                    <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200">Henüz Blog İçeriği Eklenmedi</h3>
                    <p className="mt-2 text-gray-500 dark:text-gray-400">Bu şehir için henüz bir içerik eklenmemiş. Lütfen daha sonra tekrar kontrol edin veya farklı bir şehir seçin.</p>
                  </div>
                )}
              </motion.div>
            </section>

            {/* Bottom Section */}
            <section aria-labelledby="bottom-cta-heading" className="mt-16">
              <h2 id="bottom-cta-heading" className="text-2xl font-bold text-gray-900 dark:text-white mb-6">En Çok Okunanlar</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                {featuredRecords.map(record => (
                  <div key={`featured-${record.id}`} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border dark:border-gray-700">
                    <Link to={`/blog/${record.id}`} className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">{record.title}</Link>
                    <p className="text-sm text-gray-500 mt-1">{record.category} - {record.cityName}</p>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 dark:bg-gray-800/50 p-8 rounded-xl text-center border border-blue-100 dark:border-gray-700">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Seçtiğin il için sana özel bir gezi planı hazırlayalım mı?</h3>
                <div className="mt-6">
                  <button onClick={handleGoToPlan} className="inline-block bg-blue-600 text-white font-bold px-8 py-3 rounded-full hover:bg-blue-700 transition-colors shadow-lg">
                    Bu Şehir İçin Plan Oluştur
                  </button>
                </div>
              </div>
            </section>
          </div>

          {/* Right Sidebar for Ads */}
          <aside className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-24">
              <div className="text-xs text-gray-400 text-center mb-2 uppercase tracking-wide">Reklam</div>
              <GoogleAdUnit />
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default BlogPage;
